import React, { useState, useEffect, useRef } from "react";

const PLACEHOLDERS = [
  "Technical skills you’re good at…",
  "Behaviours you’re admired for…",
  "Things people come to you for advice…",
  "Hobbies outside of work you love doing…",
];

const MAX_ITEMS = 4;

const KnownForStep = ({
  knownFor,
  onChange,
  currentInput,
  onCurrentInputChange,
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && knownFor.length < MAX_ITEMS && editingIndex === null) {
      inputRef.current.focus();
    }
  }, [knownFor.length, editingIndex]);

  useEffect(() => {
    if (editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingIndex]);

  const handleAddItem = () => {
    const trimmed = (currentInput || "").trim();
    if (!trimmed) return;
    if (knownFor.length >= MAX_ITEMS) return;

    const updated = [...knownFor, trimmed];
    onChange(updated);
    onCurrentInputChange("");

    setTimeout(() => {
      if (inputRef.current && updated.length < MAX_ITEMS) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleCurrentBlur = () => {
    const trimmed = (currentInput || "").trim();
    if (!trimmed) return;
    if (knownFor.length >= MAX_ITEMS) return;
  
    const updated = [...knownFor, trimmed];
    onChange(updated);
    onCurrentInputChange("");
  };
  

  const handleKeyDown = (e) => {
    const hasText = currentInput.trim().length > 0;

    if (
      (e.key === "Enter" || e.key === "Tab") &&
      hasText &&
      knownFor.length < MAX_ITEMS
    ) {
      e.preventDefault();
      handleAddItem();        // 1st–4th: just commit, never navigate
    }
  };

  const handleEditStart = (index) => {
    setEditingIndex(index);
    setEditValue(knownFor[index]);
  };

  const handleEditSave = (index) => {
    if (editValue.trim()) {
      const updatedItems = [...knownFor];
      updatedItems[index] = editValue.trim();
      onChange(updatedItems);
    }
    setEditingIndex(null);
    setEditValue("");
  };

  const handleEditKeyDown = (e, index) => {
    if ((e.key === "Enter" || e.key === "Tab") && editValue.trim()) {
      e.preventDefault();
      handleEditSave(index);
    } else if (e.key === "Escape") {
      setEditingIndex(null);
      setEditValue("");
    }
  };

  return (
    <div>
      <p className="font-bold font-gotham text-sm md:text-[18px] text-[#323232] mb-2">
        What would you like to highlight about your career?
      </p>
      <p className="text-sm md:text-[15px] text-[#888888] font-gotham font-normal mb-6 md:mb-8">
        E.g., Managing organisation-wide tech, Mentoring young talent, etc.
      </p>

      <div className="space-y-4">
        {Array.from({ length: MAX_ITEMS }).map((_, i) => {
          const item = knownFor[i];
          const isEditing = editingIndex === i;
          const isFilled = i < knownFor.length;

          return (
            <div key={i} className="flex items-center gap-2 h-10">
              <span
                className={`text-lg ${
                  isFilled ? "text-teal-600" : "text-[#54B0AF]"
                }`}
              >
                +
              </span>

              {isFilled && isEditing ? (
                <input
                  type="text"
                  ref={editInputRef}
                  value={editValue}
                  placeholder={PLACEHOLDERS[i]}                
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleEditKeyDown(e, i)}
                  onBlur={() => handleEditSave(i)}
                  className="w-full max-w-3xl text-[24px] text-[#54B0AF] bg-transparent border-b-2 border-[#54B0AF] outline-none placeholder-[#BBBBBB] font-gotham"
                />
              ) : isFilled ? (
                <div
                  className="w-full max-w-3xl flex items-center justify-between border-b-2 border-[#54B0AF] cursor-pointer"
                  onClick={() => handleEditStart(i)}
                >
                  <span className="font-gotham text-[24px] text-[#54B0AF] font-medium hover:text-[#3d8e8d] transition-colors">
                    {item}
                  </span>
                </div>
              ) : i === knownFor.length ? (
                // current input slot
                <input
                  type="text"
                  placeholder={PLACEHOLDERS[i]}
                  ref={inputRef}
                  value={currentInput}
                  onChange={(e) => onCurrentInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleCurrentBlur} 
                  className="w-full max-w-3xl text-[24px] font-medium text-[#54B0AF] bg-transparent border-b-2 border-[#54B0AF] outline-none placeholder-[#BBBBBB] font-gotham"
                />
              ) : (
                // future placeholders
                <div className="w-full max-w-3xl border-b-2 border-gray-300 opacity-30">
                  <span className="text-[#B8B8B8] font-gotham text-[20px] font-normal select-none">
                    {PLACEHOLDERS[i]}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KnownForStep;
